<template>
  <div id="SideBar" :class="{ show: show }">
    <div v-if="isLoading" class="spinner-container">
      <b-spinner></b-spinner>
    </div>
    <div v-else class="sideBarInner" :class="{ showInner: show }">
      <div class="top">
        <div class="option" @click="selectedOption = 0" :class="{ selected: selectedOption === 0 }">
          {{ showReminders ? "Unread" : "Upcoming" }}
        </div>
        <div class="option" @click="selectedOption = 1" :class="{ selected: selectedOption === 1 }">
          {{ showReminders ? "Read" : "Past" }}
        </div>
      </div>
      <div v-if="showAppointments" class="appointments-list">
        <div v-if="appointmentsToDisplay.length === 0" class="noAppts">
          You have no appointments to view
        </div>
        <AppointmentCard v-for="(appt, i) in appointmentsToDisplay" :key="appt.appointmentId || i" :item="appt"
          :upcoming="selectedOption === 0" :setCurrentBookingId="setCurrentBookingId" />
      </div>
    </div>
    <FeedbackModal :currentBookingId="currentBookingId" />
  </div>
</template>
<script>
import AppointmentCard from "./AppointmentCard.vue";
import EventBus from "../helpers/eventbus";
import Notification from "./Notification.vue";
import FeedbackModal from "@/components/Home/New/FeedbackModal.vue";
import { parseAppointmentTime } from "@/utils/appointmentTimezone";
import { mapState } from "vuex";

const DNA_CUTOFF_MS = 15 * 60 * 1000;

const isCancelledAppointment = (booking) => {
  const appointmentStatus = String(
    booking?.appointmentStatus ||
      booking?.appointment_status ||
      booking?.status ||
      "",
  ).trim().toLowerCase();

  return appointmentStatus === "cancelled" || appointmentStatus === "canceled";
};

export default {
  components: {
    FeedbackModal,
    Notification,
    AppointmentCard
  },
  props: ["showReminders", "showAppointments"],
  data() {
    return {
      nav_height: 0,
      selectedOption: 0,
      readNotifications: [],
      unreadNotifications: [],
      isLoading: false,
      appointments: [],
      upcomingAppointments: [],
      pastAppointments: [],
      currentBookingId: null
    };
  },
  mounted() {
    let self = this;
    EventBus.$on("getAppointments", () => {
      self.getAppointments();
    });
  },
  watch: {
    showAppointments(isOpen) {
      if (isOpen) this.getAppointments();
    }
  },
  computed: {
    show() {
      return this.showReminders || this.showAppointments;
    },
    appointmentsToDisplay() {
      return this.selectedOption === 0
        ? this.upcomingAppointments
        : this.pastAppointments;
    },
  },
  methods: {
    async getAppointments() {
      try {
        if (this.isLoading) return;
        this.isLoading = true;

        const res = await this.api({ path: "api/u/dmh/get-all-appointments" });
        const rawAppointments = res?.appointments ?? [];

        this.appointments = rawAppointments.map((booking) => {
          const startTime = parseAppointmentTime(booking.startTime);
          const endTime = parseAppointmentTime(booking.endTime);

          return {
            ...booking,
            startDublinMs: startTime ? startTime.valueOf() : 0,
            endDublinMs: endTime ? endTime.valueOf() : 0,
          };
        });
        const nowMs = Date.now();

        this.pastAppointments = this.appointments
          .filter((booking) => nowMs > booking.startDublinMs + DNA_CUTOFF_MS)
          .sort((a, b) => b.startDublinMs - a.startDublinMs);

        this.upcomingAppointments = this.appointments
          .filter((booking) => {
            return (
              nowMs <= booking.startDublinMs + DNA_CUTOFF_MS &&
              !isCancelledAppointment(booking)
            );
          })
          .sort((a, b) => a.startDublinMs - b.startDublinMs);
        this.$store.commit("setAppointments", this.appointments);
      } catch (err) {
        this.appointments = [];
        this.upcomingAppointments = [];
        this.pastAppointments = [];
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    setCurrentBookingId(bookingId) {
      this.currentBookingId = bookingId;
    }
  }
};
</script>
<style lang="less">
#SideBar {
  position: absolute;
  right: 0;
  width: 0;
  height: 100%;
  z-index: 400;
  transition: width 0.25s ease-in;
  background-color: #f9f9f9;

  .appointments-list {
    height: 100%;
    max-height: 100%;
    padding: 24px 16px 80px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 20px;
    overflow: scroll;
  }

  .notifications-list {
    height: auto;
    overflow: scroll;
    padding: 24px 16px 80px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 20px;
  }

  .spinner-container {
    color: #122130;
    padding: 20px;
    display: flex;
    justify-content: center;
  }

  .sideBarInner {
    opacity: 0;
    display: flex;
    flex-direction: column;
    transition: none;
    height: 100%;
    max-height: 100%;

    &.showInner {
      transition: opacity 0.35s ease-in 0.25s;
      opacity: 1;
    }
  }

  .noAppts {
    color: #333333;
    font-size: 1.5rem;
  }

  &.show {
    max-width: 400px;
    width: 100%;
  }

  .top {
    background-color: #fff;
    display: grid;
    grid-template-columns: 1fr 1fr;

    .option {
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      height: 73px;
      font-size: 1.4rem;
      color: #403d56;
      cursor: pointer;
      border-bottom: #d9d9d9 2px solid;

      &.selected {
        border-bottom: var(--brand-primary) 2px solid;
        color: var(--brand-primary);
        font-family: FilsonProBold, Arial, Helvetica, sans-serif;
      }
    }
  }
}
</style>
